import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';

import styled from "@emotion/styled";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import TitleLink from '../../components/TitleLink';
import { getUsers } from '../../api/APIServices';
import { USER_TYPE } from '../../constants';
import NumberCircle from '../../components/NumberCircle';
import { completePercent, getStatusColor } from '../../utils/utils';
import dayjs from 'dayjs';

const TableRowStyled = styled(TableRow)`
  &:nth-of-type(odd) {
    background-color: #ffffff;
  }
  &:nth-of-type(even) {
    background-color: #f8f8f8;
  }
  & > td {
    color: black;
  }
`;

const UsersBox = () => {
  const { t } = useTranslation('global');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { users } = useSelector((state) => state.users);

  const [sortedUsers, setSortedUsers] = useState([]);
  const [sortField, setSortField] = useState('');
  const [isAsc, setIsAsc] = useState(true);

  // Fetch users
  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  // Filter out clinician users & set initial data
  useEffect(() => {
    const filteredUsers = users.filter(
      (user) => user.userType !== USER_TYPE.CLINICIAN
    );

    // Default sort by enrollmentDate DESC
    const sorted = [...filteredUsers].sort(
      (a, b) => new Date(b.enrollmentDate) - new Date(a.enrollmentDate)
    );

    setSortedUsers(sorted);
  }, [users]);

  // Sorting handler
  const sortBy = (field) => {
    const asc = sortField === field ? !isAsc : true;

    const sorted = [...sortedUsers].sort((a, b) => {
      // Date sorting
      if (field === 'enrollmentDate') {
        return asc
          ? new Date(a[field]) - new Date(b[field])
          : new Date(b[field]) - new Date(a[field]);
      }

      // String sorting
      if (a[field] < b[field]) return asc ? -1 : 1;
      if (a[field] > b[field]) return asc ? 1 : -1;
      return 0;
    });

    setSortField(field);
    setIsAsc(asc);
    setSortedUsers(sorted);
  };

  const showUserDetails = (userId) => {
    navigate(`/user_details/${userId}`);
  };

  return (
    <div className="full-container">
      <TitleLink
        boxTitle={t('users')}
        subTitle={t('lable_sort_list')}
        enableLink={false}
      />

      <TableContainer
        sx={{
          maxHeight: 630,
          "& .MuiTableCell-root": {
            border: '1px solid #f3f3f3'
          },
          "&::-webkit-scrollbar": {
            width: 6
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "white"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 2
          }
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead
            sx={{
              "& th": {
                backgroundColor: "#def1fc",
                color: "black",
                fontWeight: 600,
                cursor: "pointer"
              },
              "& th:hover": {
                backgroundColor: "#c9e8fa"
              }
            }}
          >
            <TableRow>
              <TableCell align="center" colSpan={3} />
              <TableCell align="center" colSpan={4}>
                {t('improvement')} (%)
              </TableCell>
              <TableCell align="center" colSpan={3}>
                {t('compliance')} (%)
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell onClick={() => sortBy('firstName')}>
                {t('first_name')}
              </TableCell>
              <TableCell onClick={() => sortBy('lastName')}>
                {t('last_name')}
              </TableCell>
              <TableCell onClick={() => sortBy('enrollmentDate')}>
                {t('enrollment_date')}
              </TableCell>

              <TableCell>{t('tug')}</TableCell>
              <TableCell>STS</TableCell>
              <TableCell>{t('pain')}</TableCell>
              <TableCell>{t('sane')}</TableCell>

              <TableCell>{t('education')}</TableCell>
              <TableCell>{t('exercise')}</TableCell>
              <TableCell>{t('questionnaire')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedUsers.map((user) => (
              <TableRowStyled
                key={user.userId}
                hover
                onClick={() => showUserDetails(user.userId)}
              >
                <TableCell>
                  <Link style={{ color: '#028DD5', textDecoration: 'none' }}>
                    {user.firstName}
                  </Link>
                </TableCell>

                <TableCell>
                  <Link style={{ color: '#028DD5', textDecoration: 'none' }}>
                    {user.lastName}
                  </Link>
                </TableCell>

                <TableCell>
                  {dayjs(user.enrollmentDate).format('MM/DD/YYYY')}
                </TableCell>

                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />

                <TableCell>
                  <NumberCircle color={getStatusColor(user.compliance.education)}>
                    {completePercent(user.compliance.education)}
                  </NumberCircle>
                </TableCell>

                <TableCell>
                  <NumberCircle color={getStatusColor(user.compliance.exercise)}>
                    {completePercent(user.compliance.exercise)}
                  </NumberCircle>
                </TableCell>

                <TableCell>
                  <NumberCircle
                    color={getStatusColor(user.compliance.questionnaire)}
                  >
                    {completePercent(user.compliance.questionnaire)}
                  </NumberCircle>
                </TableCell>
              </TableRowStyled>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default UsersBox;
